/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { Platform } from 'react-native';
import { useQueryClient } from '@tanstack/react-query';
import { useAppConfig } from '@airasia-rn/ui';
import { useSetting } from '@airasia-rn/account/hooks';
import { useSuperAppContext } from '@airasia-rn/ui/context';
import { useSearchViewFeatureFlag } from './useSearchViewFeatureFlag';
import {
  FILTER_STATE_KEY,
  MAX_VALUE,
  MIN_VALUE,
  MOVE_HOTEL_WEB_BASE_PATH,
  MOVE_HOTEL_WEB_HOSTS,
  OLD_HOTEL_LIST_PAGE_BASE_URLS,
  SORT_BY,
} from '../configs/constants';
import { FEATURE_FLAG_HOTEL_HOME_TILE_REDIRECT_ENABLED } from '../configs/featureFlagConstant';
import { useHotelConfig } from '../configs/HotelConfigProvider';
import {
  dispatchHotelSearchUrlChange,
  replaceHotelListSearchUrl,
} from '../utils/hotelListSearchUrl';
import {
  useOptionalHotelsNavigation,
  useOptionalHotelsRoutes,
  useHotelsSearchParams,
} from '../context/HotelsNavigationContext';
import { resolveDetailsRedirectHref } from '../context/HotelsNavigationContext/detailNavigation';
import {
  getSelectedHotelIdFromUrl,
  removeEmptySelectedHotelQueryParams,
  syncSelectedHotelFilter,
} from '../utils/selectedHotelFilter';
import type { HotelFilters } from '../types';

type UseHandleHotelListNavigationOptions = {
  source?: 'hotels' | 'home';
};

type RouteQueryParams = Record<string, string | string[] | undefined>;

const getQueryParamValue = (
  params: RouteQueryParams,
  ...keys: string[]
): string | undefined => {
  for (const key of keys) {
    const value = params[key];
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
    if (Array.isArray(value) && value.length > 0) {
      return value[0];
    }
  }
  return undefined;
};

const isEditSearchListReturn = (params: RouteQueryParams) =>
  getQueryParamValue(params, 'from_listing') === '1' ||
  getQueryParamValue(params, 'from_mapview') === '1';

const isSettingEnabled = (value?: string) => {
  const normalizedValue = value?.trim().toLowerCase();
  return normalizedValue === '1' || normalizedValue === 'true';
};

const buildHostedMoveWebUrl = (baseUrl: string, routePath: string) => {
  // Trailing slash required so `search?…` resolves under `/hotel/`, not replacing it.
  const baseWithSlash = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  return new URL(routePath.replace(/^\//, ''), baseWithSlash).toString();
};

const stripMoveHotelMountPrefix = (routePath: string) => {
  if (routePath === MOVE_HOTEL_WEB_BASE_PATH) {
    return '/';
  }

  if (routePath.startsWith(`${MOVE_HOTEL_WEB_BASE_PATH}/`)) {
    return routePath.slice(MOVE_HOTEL_WEB_BASE_PATH.length);
  }

  return routePath;
};

const getCurrentLocationBase = () => {
  if (typeof window === 'undefined') {
    return 'https://www.airasia.com/';
  }

  return window.location.href || 'https://www.airasia.com/';
};

const normalizePathnameForComparison = (pathname: string) => {
  const normalizedPathname = pathname.replace(/\/+$/, '');
  return normalizedPathname.length > 0 ? normalizedPathname : '/';
};

const hasMountedRoutePath = (
  currentPathname: string,
  targetPathname: string,
) => {
  const normalizedCurrentPathname =
    normalizePathnameForComparison(currentPathname);
  const normalizedTargetPathname =
    normalizePathnameForComparison(targetPathname);

  return (
    normalizedTargetPathname !== '/' &&
    normalizedCurrentPathname !== normalizedTargetPathname &&
    normalizedCurrentPathname.endsWith(normalizedTargetPathname)
  );
};

const resolveTargetUrl = (url: string, currentUrl: URL) => {
  if (url.startsWith('http')) {
    return new URL(url);
  }

  // When the app is mounted under a base path (e.g. /hotel),
  // preserve the current pathname for query-only updates that are built as "/?...".
  if (url.startsWith('/?') && currentUrl.pathname !== '/') {
    return new URL(`${currentUrl.pathname}${url.slice(1)}`, currentUrl);
  }

  const targetUrl = new URL(url, currentUrl);

  // Expo Router paths omit the configured web base URL. When the app is
  // mounted at `/hotel`, `/search?...` and `/hotel/search?...` are the same
  // logical route, so retain the mounted browser pathname and update only its
  // query instead of navigating and remounting the listing screen.
  if (
    url.startsWith('/') &&
    !url.startsWith('//') &&
    hasMountedRoutePath(currentUrl.pathname, targetUrl.pathname)
  ) {
    targetUrl.pathname = currentUrl.pathname;
  }

  return targetUrl;
};

const FILTER_QUERY_KEYS = [
  'guestRating',
  'F_R',
  'category',
  'propertyType',
  'payment_method',
  'paymentType',
  'star_rating',
  'hotelClass',
  'amenity',
  'rate_amenity',
  'landmark',
] as const;

const getDefaultSearchFilters = (
  selectedHotelId = '',
  selectedHotelName = '',
): HotelFilters => ({
  priceRange: [MIN_VALUE, MAX_VALUE],
  selectedPriceRanges: [],
  sortBy: SORT_BY.DEFAULT,
  selectedPromos: [],
  selectedRateAmenities: [],
  guestRating: '',
  selectedHotelId,
  selectedHotelName,
  starRating: [],
  selectedAmenities: [],
  selectedPropertyTypes: [],
  selectedPaymentPolicies: [],
  selectedLandmark: undefined,
});

const hasActiveListFilterParams = (url: URL) => {
  const sort = url.searchParams.get('sort');

  if (sort && sort !== SORT_BY.DEFAULT) {
    return true;
  }

  return FILTER_QUERY_KEYS.some((key) =>
    url.searchParams.getAll(key).some((value) => value.trim().length > 0),
  );
};

const syncFilterCacheForListNavigation = (
  queryClient: ReturnType<typeof useQueryClient>,
  targetUrl: URL,
) => {
  const selectedHotelId = getSelectedHotelIdFromUrl(targetUrl.toString());
  const urlDestinationName =
    targetUrl.searchParams.get('destination_name')?.trim() || '';
  const cachedFilters =
    queryClient.getQueryData<HotelFilters>(FILTER_STATE_KEY);
  const selectedHotelName = selectedHotelId
    ? cachedFilters?.selectedHotelId === selectedHotelId
      ? cachedFilters.selectedHotelName || urlDestinationName
      : urlDestinationName
    : '';

  if (hasActiveListFilterParams(targetUrl)) {
    // The target URL already carries its own filter params (guest rating,
    // amenities, etc.) — leave those alone instead of blowing them away with
    // defaults, but the selected-hotel id/name must still track the URL,
    // otherwise an in-place edit search to a new hotel leaves the previous
    // hotel's id cached, so the listing never highlights (or requests)
    // the newly searched property while any other filter is active.
    queryClient.setQueryData<HotelFilters>(FILTER_STATE_KEY, (old) =>
      old
        ? {
            ...old,
            selectedHotelId,
            selectedHotelName,
            ...(old.selectedHotelId !== selectedHotelId
              ? { safetyNetSelectedHotel: undefined }
              : {}),
          }
        : getDefaultSearchFilters(selectedHotelId, selectedHotelName),
    );
    queryClient.setQueryData(['isUrlRestored'], true);
    return;
  }

  queryClient.setQueryData<HotelFilters>(
    FILTER_STATE_KEY,
    getDefaultSearchFilters(selectedHotelId, selectedHotelName),
  );
  queryClient.setQueryData(['isUrlRestored'], true);
};

export const useHandleHotelListNavigation = (
  options?: UseHandleHotelListNavigationOptions,
) => {
  const navigation = useOptionalHotelsNavigation();
  const routes = useOptionalHotelsRoutes();
  const routeParams = useHotelsSearchParams();
  const queryClient = useQueryClient();
  const { config: appConfig } = useAppConfig();
  const { config: hotelConfig } = useHotelConfig();
  const { geoId: countryCodeFromContext } = useSuperAppContext();
  const source = options?.source ?? hotelConfig?.searchSource ?? 'hotels';
  const { hotel_move_list_page_enabled, isSettingsLoading } =
    useSearchViewFeatureFlag();
  const {
    value: isHomeRedirectEnabled,
    isSettingsLoading: isHomeRedirectLoading,
  } = useSetting(FEATURE_FLAG_HOTEL_HOME_TILE_REDIRECT_ENABLED);
  const isHomeRedirectEnabledFlag = isSettingEnabled(isHomeRedirectEnabled);
  const shouldUseMoveListForSource =
    source === 'home'
      ? isHomeRedirectEnabledFlag
      : hotel_move_list_page_enabled === true;
  const isActiveFlagLoading =
    source === 'home' ? isHomeRedirectLoading : isSettingsLoading;

  const shouldUseNewWebListing =
    Platform.OS === 'web' && !isActiveFlagLoading && shouldUseMoveListForSource;
  const legacyListBaseUrl =
    appConfig.env === 'prd'
      ? OLD_HOTEL_LIST_PAGE_BASE_URLS.prod
      : OLD_HOTEL_LIST_PAGE_BASE_URLS.stg;
  const moveHomeHost =
    appConfig.env === 'prd'
      ? MOVE_HOTEL_WEB_HOSTS.prd
      : appConfig.env === 'stg'
        ? MOVE_HOTEL_WEB_HOSTS.stg
        : MOVE_HOTEL_WEB_HOSTS.dev;

  const getHotelListRoutePath = () => {
    if (Platform.OS !== 'web') {
      return routes?.HOTELS_LIST ?? '/search';
    }

    if (!shouldUseNewWebListing) {
      return legacyListBaseUrl;
    }

    return routes?.HOTELS_LIST ?? '/search';
  };

  const navigateToHotelListUrl = (url: string) => {
    if (Platform.OS === 'web') {
      const currentUrl =
        typeof window !== 'undefined'
          ? new URL(getCurrentLocationBase())
          : undefined;

      if (shouldUseNewWebListing) {
        if (source === 'home') {
          const pathAndQuery = url.startsWith('http')
            ? `${new URL(url).pathname}${new URL(url).search}`
            : url;
          const hostedUrl = buildHostedMoveWebUrl(
            moveHomeHost,
            stripMoveHotelMountPrefix(pathAndQuery),
          );
          // Home source must transition into hotels-web shell explicitly.
          // Using SPA replace can stay inside the host app and no-op visually.
          window.location.assign(hostedUrl);
          return;
        }

        // Replace instead of push to avoid stacking duplicate listing screens
        // with different query params on web. When the navigation context is
        // mounted (hotels-web/dev-app/embed) delegate to its dedicated methods;
        // otherwise (e.g. home) fall back to the shared util directly.
        if (currentUrl) {
          const targetUrl = resolveTargetUrl(url, currentUrl);
          syncFilterCacheForListNavigation(queryClient, targetUrl);
          if (
            currentUrl.origin === targetUrl.origin &&
            normalizePathnameForComparison(currentUrl.pathname) ===
              normalizePathnameForComparison(targetUrl.pathname)
          ) {
            if (navigation) {
              navigation.replaceListSearchParams(targetUrl.toString());
            } else {
              const { router: nativeRouter } = require('expo-router');
              replaceHotelListSearchUrl(targetUrl, nativeRouter);
            }
            return;
          }
        }

        if (navigation) {
          navigation.replaceListUrl(url);
        } else {
          const { router: nativeRouter } = require('expo-router');
          nativeRouter.replace(url);
          dispatchHotelSearchUrlChange();
        }
        return;
      }

      window.location.assign(url);
      return;
    }

    const targetUrl = new URL(url, 'https://www.airasia.com/');
    syncFilterCacheForListNavigation(queryClient, targetUrl);
    const selectedHotelId = getSelectedHotelIdFromUrl(url);
    if (!selectedHotelId) {
      syncSelectedHotelFilter(queryClient, '', '');
    }

    const listPageUrl = removeEmptySelectedHotelQueryParams(url);

    const shouldReplaceEditSearchList =
      shouldUseMoveListForSource &&
      source !== 'home' &&
      isEditSearchListReturn(routeParams as RouteQueryParams);

    if (shouldReplaceEditSearchList) {
      if (navigation) {
        if (navigation.canGoBack?.()) {
          navigation.dismissTo(listPageUrl);
        } else {
          navigation.replaceListUrl(listPageUrl);
        }
        return;
      }

      const { router: nativeRouter } = require('expo-router');

      if (nativeRouter.canGoBack?.()) {
        nativeRouter.dismissTo(listPageUrl);
      } else {
        nativeRouter.replace(listPageUrl);
      }
      return;
    }

    if (navigation) {
      navigation.navigateToListUrl(listPageUrl);
      return;
    }

    const { router: nativeRouter } = require('expo-router');
    nativeRouter.push(listPageUrl);
  };

  const navigateToHotelDetailsUrl = (url: string) => {
    if (Platform.OS === 'web') {
      let absoluteTargetUrl = url;

      if (
        !absoluteTargetUrl.startsWith('http') &&
        typeof window !== 'undefined'
      ) {
        const baseLocation = new URL(getCurrentLocationBase());
        absoluteTargetUrl = new URL(
          absoluteTargetUrl,
          baseLocation.origin,
        ).toString();
      }

      try {
        const parsedUrl = new URL(absoluteTargetUrl);
        if (appConfig.env !== 'prd') {
          // Legacy deal/API links still use hotels.* or www.* staging hosts.
          // Move hotels-web is served from web.* with the shared /hotel mount.
          if (
            parsedUrl.hostname === 'hotels.stgairasia.com' ||
            parsedUrl.hostname === 'www.stgairasia.com'
          ) {
            parsedUrl.hostname = 'web.stgairasia.com';
          } else if (
            parsedUrl.hostname === 'hotels.devairasia.com' ||
            parsedUrl.hostname === 'www.devairasia.com'
          ) {
            parsedUrl.hostname = 'web.devairasia.com';
          }

          if (
            (parsedUrl.hostname === 'web.stgairasia.com' ||
              parsedUrl.hostname === 'web.devairasia.com') &&
            !parsedUrl.pathname.startsWith(`${MOVE_HOTEL_WEB_BASE_PATH}/`) &&
            parsedUrl.pathname !== MOVE_HOTEL_WEB_BASE_PATH
          ) {
            parsedUrl.pathname = `${MOVE_HOTEL_WEB_BASE_PATH}${
              parsedUrl.pathname.startsWith('/')
                ? parsedUrl.pathname
                : `/${parsedUrl.pathname}`
            }`;
          }
        }
        if (source === 'home') {
          const relativeRoute = stripMoveHotelMountPrefix(
            `${parsedUrl.pathname}${parsedUrl.search}`,
          );
          const hostedUrl = buildHostedMoveWebUrl(moveHomeHost, relativeRoute);
          window.location.assign(hostedUrl);
          return;
        }

        // On mobile web browser instances or regular desktop layout environments
        window.location.assign(parsedUrl.toString());
      } catch (error) {
        window.location.assign(absoluteTargetUrl);
      }
      return;
    }

    const detailsRoutePrefix =
      navigation?.routes.details ?? routes?.details ?? '/';
    const detailsHref = resolveDetailsRedirectHref(url, detailsRoutePrefix);

    if (navigation) {
      navigation.redirectTo(detailsHref);
      return;
    }

    const { router: nativeRouter } = require('expo-router');
    nativeRouter.push(detailsHref);
  };

  return {
    getHotelListRoutePath,
    navigateToHotelListUrl,
    navigateToHotelDetailsUrl,
    shouldUseNewWebListing,
    shouldUseMoveListForSource,
  };
};
